/**
 * The phone's side of driving: keeping the screen on during a shift, the job offer alert
 * (a chime and a buzz), and handing off to a navigation app or the phone to call.
 */
import { useEffect, useSyncExternalStore } from "react";
import type { LatLng } from "./types";

// ---------------------------------------------------------------------------
// Keeping the screen on
// ---------------------------------------------------------------------------

/**
 * Keeps the screen on while `active`. Location sharing (and the offer alert) only work
 * while the app is on screen, and drivers' phones sit in a cradle anyway.
 */
export function useWakeLock(active: boolean) {
  useEffect(() => {
    if (!active || !("wakeLock" in navigator)) return;
    let lock: WakeLockSentinel | null = null;
    let done = false;
    const request = () => {
      if (done || document.visibilityState !== "visible") return;
      navigator.wakeLock
        .request("screen")
        .then((sentinel) => {
          if (done) void sentinel.release();
          else lock = sentinel;
        })
        .catch(() => undefined);
    };
    // The lock goes when the app leaves the screen: take it again when it's back.
    document.addEventListener("visibilitychange", request);
    request();
    return () => {
      done = true;
      document.removeEventListener("visibilitychange", request);
      void lock?.release().catch(() => undefined);
    };
  }, [active]);
}

// ---------------------------------------------------------------------------
// The offer alert
// ---------------------------------------------------------------------------

let audio: AudioContext | null = null;
const soundListeners = new Set<() => void>();
const notifySound = () => {
  for (const listener of soundListeners) listener();
};

/**
 * Browsers only play sound once the person has tapped the page: any tap gets it ready.
 * (Again after a phone call or the like, which can stop it.)
 */
export function unlockSound() {
  try {
    audio ??= new AudioContext();
    if (audio.state !== "running") void audio.resume().then(notifySound, () => undefined);
  } catch {
    // No Web Audio: the buzz and notifications still work.
  }
}

if (typeof document !== "undefined") {
  document.addEventListener("pointerdown", unlockSound, { capture: true, passive: true });
}

const soundState = () => audio?.state === "running";

/** Whether the offer chime can play yet. */
export function useSoundReady(): boolean {
  return useSyncExternalStore(
    (listener) => {
      soundListeners.add(listener);
      return () => soundListeners.delete(listener);
    },
    soundState,
    () => false,
  );
}

/** Three quick notes, loud enough to hear over the road. */
function chime(context: AudioContext) {
  const start = context.currentTime + 0.02;
  for (const [offset, frequency] of [
    [0, 988],
    [0.16, 784],
    [0.32, 1175],
  ] as const) {
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = "triangle";
    oscillator.frequency.value = frequency;
    gain.gain.setValueAtTime(0.0001, start + offset);
    gain.gain.exponentialRampToValueAtTime(0.7, start + offset + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + offset + 0.15);
    oscillator.connect(gain).connect(context.destination);
    oscillator.start(start + offset);
    oscillator.stop(start + offset + 0.16);
  }
}

/** The alert for a new offer, every couple of seconds until the returned function stops it. */
export function startOfferAlert(): () => void {
  const play = () => {
    if (audio?.state === "running") chime(audio);
    navigator.vibrate?.([300, 120, 300]);
  };
  play();
  const timer = setInterval(play, 2_000);
  return () => {
    clearInterval(timer);
    navigator.vibrate?.(0);
  };
}

/** Plays the chime once, to check the volume. */
export function testSound() {
  unlockSound();
  // A moment for the audio to start the first time.
  setTimeout(() => {
    if (audio?.state === "running") chime(audio);
    navigator.vibrate?.([300, 120, 300]);
  }, 60);
}

// ---------------------------------------------------------------------------
// Navigation and calls
// ---------------------------------------------------------------------------

const coords = (p: LatLng) => `${p.lat.toFixed(6)},${p.lng.toFixed(6)}`;

/**
 * Turn-by-turn directions in the driver's own navigation app. Google Maps takes the stops
 * on the way too; Waze and Apple Maps go straight to the destination.
 */
export function directions(to: LatLng, via: LatLng[] = []): { name: string; url: string }[] {
  const at = coords(to);
  const waypoints = via.length ? `&waypoints=${encodeURIComponent(via.map(coords).join("|"))}` : "";
  return [
    {
      name: "Google Maps",
      url: `https://www.google.com/maps/dir/?api=1&destination=${at}${waypoints}&travelmode=driving`,
    },
    { name: "Waze", url: `https://waze.com/ul?ll=${at}&navigate=yes` },
    { name: "Apple Maps", url: `https://maps.apple.com/?daddr=${at}&dirflg=d` },
  ];
}

/**
 * Calls the passenger with the driver's number withheld: 141 first, for UK numbers.
 * (Numbers from abroad can't be withheld this way, so they're called as they are.)
 */
export function withheldCall(e164: string): string {
  return e164.startsWith("+44") ? `tel:141${`0${e164.slice(3)}`}` : `tel:${e164}`;
}
